import { useState } from 'react';
import { useAuth } from '@/core/auth/AuthContext';
import { PageLayout } from '@/shared/layout/PageLayout';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Input } from '@/components/ui';
import { Download, Search } from 'lucide-react';
import { AuditKpis } from './components/AuditKpis';
import { AuditFilters } from './components/AuditFilters';
import { AuditTable } from './components/AuditTable';
import { useAuditLogs, useAuditStats } from './hooks/useAuditLogs';
import { TablePagination } from '@/components/ui/TablePagination';

export default function AuditListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';

  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({});

  const {
    logs, isLoading, total, filters, setFilters, updatePage, updatePageSize
  } = useAuditLogs(companyId);

  const { data: stats, isLoading: statsLoading } = useAuditStats(
    companyId,
    dateRange.start,
    dateRange.end
  );

  return (
    <PageLayout
      title="Nhật ký hệ thống"
      description="Theo dõi tất cả các hoạt động quan trọng trong hệ thống."
      isLoading={false}
    >
      <div className="flex flex-col h-full gap-6">

        {/* Header Actions */}
        <div className="flex flex-col md:flex-row md:items-center justify-end gap-3">
          <AuthButton
            module="audit_logs"
            action="export"
            behavior="disable"
            permissionReason="Bạn không có quyền xuất báo cáo nhật ký"
            variant="outline"
            className="h-9 sm:text-sm text-xs border-slate-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Xuất báo cáo
          </AuthButton>
        </div>

        {/* KPI Section – Real data, no mock */}
        <AuditKpis
          stats={stats ?? { total: 0, uniqueUsers: 0, successCount: 0, failedCount: 0, uniqueEntities: 0 }}
          isLoading={statsLoading}
        />

        {/* Filter Bar – 1 hàng ngang chuẩn SaaS */}
        <div className="flex flex-col lg:flex-row gap-3 items-center bg-white p-3 rounded-md border border-slate-200">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Tìm theo nội dung hành động..."
              value={filters.search || ''}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
              className="pl-9 h-9 sm:text-sm text-xs"
            />
          </div>
          <AuditFilters filters={filters} onFilterChange={(f) => setFilters(prev => ({ ...prev, ...f, page: 1 }))} />
        </div>

        {/* Main Table Section */}
        <div className="flex-1 bg-white rounded-md border border-slate-100">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse flex items-center gap-4 py-3 border-b border-slate-50">
                  <div className="w-8 h-8 rounded-full bg-slate-100 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-slate-100 rounded w-2/3" />
                    <div className="h-2.5 bg-slate-100 rounded w-1/3" />
                  </div>
                  <div className="h-3 bg-slate-100 rounded w-24 shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <AuditTable logs={logs} />
              <div className="p-3 border-t border-slate-100">
                <TablePagination
                  total={total}
                  page={filters.page ?? 1}
                  pageSize={filters.pageSize ?? 20}
                  onPageChange={updatePage}
                  onPageSizeChange={updatePageSize}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
